import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import type { ToolUseItem } from '../../../contracts/harness.types';
import { readToolResultText } from '../../../contracts/toolResult';
import { ExportGeneratingCard } from './ExportGeneratingCard';
import { InlineToolCallRow } from './InlineToolCallRow';

import type { Artifact } from '../../../data/projects/artifacts/state';
import { isRecoverableContextPrompt } from '../../../data/projects/components/projectChatMessage.helpers';
import { toBlockStatus } from '../../../data/projects/timeline/genericToolUseStatus';

import { GenericToolUseRow, type GenericToolUseRowProps } from './GenericToolUseRow';

export interface RenderGenerateConsultNoteToolUseDeps {
  onOpenArtifact: (artifact: Artifact) => void;
  intl: IntlShape;
  /** What the generic row the failures fall back to needs from the app. */
  resolveArtifact: GenericToolUseRowProps['resolveArtifact'];
  renderConnectRequired: GenericToolUseRowProps['renderConnectRequired'];
}

/**
 * Bespoke presentation for ``generate_consult_note``:
 * - template-pick guardrails render as nothing (the ``pick_template`` card owns UX)
 * - running → generating card
 * - success → inline confirmation only (note lives on the session Note tab)
 * - other failures → generic destructive row
 */
export const renderGenerateConsultNoteToolUse = (
  item: ToolUseItem,
  deps: RenderGenerateConsultNoteToolUseDeps
): ReactNode | null => {
  if (item.name !== 'generate_consult_note') return null;

  const status = toBlockStatus(item.status);
  const errorText = readToolResultText(item);

  if (status === 'error' && isRecoverableContextPrompt(errorText)) {
    return null;
  }

  if (status === 'running') {
    // NOT a wire join, and the adapter is deliberately absent here: this site
    // names the kind itself rather than reading one off a tool result, so what it
    // passes is already the agents union's own member. Routing a literal through
    // the adapter would mean typing it as the evidence door's first, which is the
    // coupling the standard removes.
    return (
      <ExportGeneratingCard
        artefactType="word"
        title={deps.intl.formatMessage({
          defaultMessage: 'Generating consult note',
          id: '6p79lyOELb',
          description: 'Timeline card while generate_consult_note runs',
        })}
      />
    );
  }

  if (status === 'success') {
    return (
      <InlineToolCallRow
        toolName={item.name}
        toolInput={item.input}
        status="success"
        toolCallDisplay={item.toolCallDisplay}
      />
    );
  }

  if (status === 'error') {
    return (
      <GenericToolUseRow
        item={item}
        onOpenArtifact={deps.onOpenArtifact}
        resolveArtifact={deps.resolveArtifact}
        renderConnectRequired={deps.renderConnectRequired}
      />
    );
  }

  return null;
};
